/**
 * Conversion scrub / manual-review workflow — data-layer client (Feature #139).
 *
 * Thin typed wrapper over `apiClient` for the /api/v1/scrub-workflow surface.
 * Keep this dependency-free beyond `apiClient` — no React, no TanStack — so
 * the derivation + filter helpers stay unit-testable in isolation.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Types — mirror the backend row shape exactly (see
// apps/api/src/routes/scrub-workflow.ts).
// ---------------------------------------------------------------------------

export type ScrubStatus = "pending" | "approved" | "rejected";

export const SCRUB_STATUSES: readonly ScrubStatus[] = [
	"pending",
	"approved",
	"rejected",
] as const;

export type QueueFilter = ScrubStatus | "all";

export const QUEUE_FILTERS: readonly QueueFilter[] = [
	"pending",
	"approved",
	"rejected",
	"all",
] as const;

export function isQueueFilter(v: unknown): v is QueueFilter {
	return (
		typeof v === "string" && (QUEUE_FILTERS as readonly string[]).includes(v)
	);
}

export type AuditFilter = "all" | "approved" | "rejected";

export const AUDIT_FILTERS: readonly AuditFilter[] = [
	"all",
	"approved",
	"rejected",
] as const;

export function isAuditFilter(v: unknown): v is AuditFilter {
	return (
		typeof v === "string" && (AUDIT_FILTERS as readonly string[]).includes(v)
	);
}

export interface ScrubRow {
	id: string;
	tenant_id: string;
	conversion_id: string;
	status: ScrubStatus;
	reason: string | null;
	reviewed_by: string | null;
	reviewed_at: number | null;
	notes: string | null;
	created_at: number;
}

export interface QueueResponse {
	count: number;
	filters: { status: string; limit: number };
	items: ScrubRow[];
}

export interface AuditResponse {
	count: number;
	filters: { status: string; limit: number };
	items: ScrubRow[];
}

export interface ApproveResponse {
	id: string;
	status: "approved";
	reviewed_by: string | null;
	reviewed_at: number;
	notes: string | null;
}

export interface RejectResponse {
	id: string;
	status: "rejected";
	reviewed_by: string | null;
	reviewed_at: number;
	reason: string;
	notes: string | null;
}

// ---------------------------------------------------------------------------
// KPI derivation — the /queue endpoint returns the full filtered set, which
// is enough to compute today/week/pending/rejected counts client-side.
// ---------------------------------------------------------------------------

export interface ScrubStats {
	today: number;
	week: number;
	pending: number;
	reviewed: number;
}

export function computeScrubStats(
	rows: readonly ScrubRow[],
	nowMs: number = Date.now(),
): ScrubStats {
	const nowSec = Math.floor(nowMs / 1000);
	const dayStart = Math.floor(nowMs / 86_400_000) * 86_400;
	const weekStart = nowSec - 7 * 86_400;

	let today = 0;
	let week = 0;
	let pending = 0;
	let reviewed = 0;

	for (const row of rows) {
		if (row.created_at >= dayStart) today += 1;
		if (row.created_at >= weekStart) week += 1;
		if (row.status === "pending") pending += 1;
		else reviewed += 1;
	}

	return { today, week, pending, reviewed };
}

// ---------------------------------------------------------------------------
// Remote bindings
// ---------------------------------------------------------------------------

const BASE = "/scrub-workflow";

export interface FetchQueueArgs {
	status: QueueFilter;
	limit?: number;
}

/** GET /api/v1/scrub-workflow/queue */
export async function fetchScrubQueue(
	args: FetchQueueArgs,
): Promise<QueueResponse> {
	return apiClient.get<QueueResponse>(`${BASE}/queue`, {
		query: { status: args.status, limit: args.limit ?? 200 },
	});
}

export interface FetchAuditArgs {
	status: AuditFilter;
	limit?: number;
}

/** GET /api/v1/scrub-workflow/audit */
export async function fetchScrubAudit(
	args: FetchAuditArgs,
): Promise<AuditResponse> {
	return apiClient.get<AuditResponse>(`${BASE}/audit`, {
		query: { status: args.status, limit: args.limit ?? 200 },
	});
}

export interface ApproveArgs {
	id: string;
	notes?: string;
}

/** POST /api/v1/scrub-workflow/approve/:id */
export async function approveScrub(
	args: ApproveArgs,
): Promise<ApproveResponse> {
	return apiClient.post<ApproveResponse>(
		`${BASE}/approve/${encodeURIComponent(args.id)}`,
		args.notes ? { notes: args.notes } : {},
	);
}

export interface RejectArgs {
	id: string;
	reason: string;
	notes?: string;
}

/** POST /api/v1/scrub-workflow/reject/:id */
export async function rejectScrub(args: RejectArgs): Promise<RejectResponse> {
	const body: { reason: string; notes?: string } = { reason: args.reason };
	if (args.notes) body.notes = args.notes;
	return apiClient.post<RejectResponse>(
		`${BASE}/reject/${encodeURIComponent(args.id)}`,
		body,
	);
}

// ---------------------------------------------------------------------------
// Presentation helpers — pure + tested.
// ---------------------------------------------------------------------------

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

export function formatScrubDate(unixSec: number | null | undefined): string {
	if (typeof unixSec !== "number" || !Number.isFinite(unixSec) || unixSec <= 0)
		return "—";
	const d = new Date(unixSec * 1000);
	return d.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

export function formatScrubDateTime(
	unixSec: number | null | undefined,
): string {
	if (typeof unixSec !== "number" || !Number.isFinite(unixSec) || unixSec <= 0)
		return "—";
	const d = new Date(unixSec * 1000);
	return d.toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

export function statusLabel(status: ScrubStatus): string {
	return status.charAt(0).toUpperCase() + status.slice(1);
}

export function isReasonValid(reason: string): {
	ok: boolean;
	error: string | null;
} {
	const trimmed = reason.trim();
	if (trimmed.length < 3) {
		return { ok: false, error: "Reason must be at least 3 characters." };
	}
	if (trimmed.length > 1000) {
		return { ok: false, error: "Reason must be 1000 characters or fewer." };
	}
	return { ok: true, error: null };
}

export const scrubQueryKeys = {
	all: ["scrub-workflow"] as const,
	queue: (status: QueueFilter) =>
		[...scrubQueryKeys.all, "queue", status] as const,
	audit: (status: AuditFilter) =>
		[...scrubQueryKeys.all, "audit", status] as const,
};
